import { Anchor } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { Missing, TextCell } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { ProductCustomFieldsItemResponseDto } from "~/gen/api"
import { useGetOperations } from "~/utils/operations"

const columnHelper = createColumnHelper<ProductCustomFieldsItemResponseDto>()

export const useProductsCustomFieldsColumns = (customFieldKeys: string[]) => {
  const navigate = useNavigate()
  const { t } = useTranslation(["products"])

  const operations = useGetOperations()

  return useMemo<ColumnDef<ProductCustomFieldsItemResponseDto, any>[]>(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: t("lists.columns.name"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => (
          <Anchor
            fz="xs"
            c="gray.9"
            onClick={() =>
              navigate(`/products/details/${info.row.original.id}`)
            }
          >
            <TextCell value={info.getValue()} />
          </Anchor>
        ),
      }),
      columnHelper.accessor("brandReference", {
        id: "brandReference",
        header: t("lists.columns.reference"),
        enableSorting: false,
        enablePinning: true,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("color", {
        header: t("lists.columns.color"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("collection", {
        header: t("lists.columns.collection"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("brand", {
        id: "brand",
        header: t("lists.columns.brand"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      columnHelper.accessor("brandType", {
        id: "type",
        header: t("lists.columns.type"),
        enableSorting: false,
        cell: (info) => <TextCell value={info.getValue()} />,
      }),
      ...customFieldKeys.map((customFieldKey) =>
        columnHelper.accessor(
          (p) =>
            (p.flattenCustomFields as Record<string, string>)[customFieldKey],
          {
            id: "cfd-" + customFieldKey,
            header: customFieldKey,
            enableSorting: false,
            cell: (info) => {
              const value = info.getValue()
              if (value === "") {
                return <Missing c="gray" />
              }
              if (value === "missing-main-component") {
                return <Missing c="gray" />
              }
              if (operations[value]) {
                return <TextCell value={operations[value].name} />
              }
              return <TextCell value={value} />
            },
          }
        )
      ),
    ],
    [t, customFieldKeys, navigate, operations]
  )
}
